import { useMemo, useState } from "react";

import { useHistory } from "react-router";

import {
  Button,
  classes,
  Icon,
  Modal,
  Text,
  toastSuccess,
  useDisclosure,
} from "@alpacahq/ui/old";
import { DataGrid, type TableColumn } from "@alpacahq/ui/old/datagrid";

import { useGetPositions, useLiquidatePosition } from "~/v2/api/hooks";
import type { Position } from "~/v2/api/rest/account";
import { SymbolLink } from "~/v2/components/SymbolLink";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { Filter } from "../../../../components/common/filter";
import { Path } from "../../../../path";
import { getCryptoBadge } from "../../../../utils/display";
import { getPrettyCash, getPrettyQty } from "../../../../utils/formatting";
import { Radio } from "../../entityOnboarding/Radio";

export const Portfolio = () => {
  const history = useHistory();
  const disclosure = useDisclosure();
  const filterKey = "filter:portfolio";

  const { product, currentAccount } = useAccountContext();

  const { positions } = useGetPositions({
    accountID: currentAccount?.id,
    product,
    options: { enabled: !!currentAccount },
  });

  const [symbol, setSymbol] = useState<string>("");
  const [cancelOrders, setCancelOrders] = useState(false);
  const [filter, setFilter] = useState<Record<string, string>>(
    JSON.parse(window.localStorage?.getItem(filterKey) || "{}"),
  );

  const onBeforeSetFilter = (newField: Record<string, string>) => {
    const next = { ...filter, ...newField };
    window.localStorage?.setItem(filterKey, JSON.stringify(next));
    setFilter(next);
  };

  const data = useMemo<Position[]>(
    () =>
      // rank positions by market_value
      [...positions]
        .sort(
          (a, b) => Number(b.market_value || 0) - Number(a.market_value || 0),
        )
        .filter((position) => {
          const keys = Object.keys(filter || {}).filter(
            (key) => filter[key] !== "all",
          );

          if (keys.length === 0) {
            return true;
          }

          return keys.every(
            (key) => position[key as keyof Position] === filter[key],
          );
        }),
    [positions, filter],
  );

  const mutation = useLiquidatePosition({
    onSuccess: () => {
      toastSuccess("Position liquidated");
    },
    onSettled: () => {
      onClose();
    },
  });

  const columns = useMemo<TableColumn<Position>[]>(
    () => [
      {
        name: "Asset",
        key: "asset",
        cellRenderer: ({ symbol, asset_class }) => (
          <SymbolLink symbol={symbol}>{getCryptoBadge(asset_class)}</SymbolLink>
        ),
      },
      {
        name: "Price",
        key: "price",
        cellRenderer: ({ current_price }) =>
          getPrettyCash(current_price, { minimumFractionDigits: 2 }),
      },
      {
        name: "Qty",
        key: "qty",
        cellRenderer: ({ qty }) => getPrettyQty(qty),
      },
      {
        name: "Market Value",
        key: "market_value",
        cellRenderer: ({ market_value }) =>
          getPrettyCash(market_value, { minimumFractionDigits: 2 }),
      },
      {
        name: "Total P/L ($)",
        key: "profit_loss",
        cellRenderer: ({ unrealized_pl }) => {
          const num = Number(unrealized_pl || 0);
          const pos = num >= 0;

          return (
            <span
              className={classes(
                "font-medium",
                pos ? "text-green-600" : "text-red-600",
              )}
            >
              {getPrettyCash(num, {
                withPrefix: true,
                minimumFractionDigits: 2,
              })}
            </span>
          );
        },
      },
    ],
    [],
  );

  const onOpen = ({ symbol }: Position) => {
    setSymbol(symbol);
    document.getElementById("root")?.classList.add("blur");
    disclosure.onOpen();
  };

  const onClose = () => {
    setSymbol("");
    document.getElementById("root")?.classList.remove("blur");
    disclosure.onClose();
  };

  const onChange = (isChecked: boolean) => setCancelOrders(isChecked);

  const onClick = () => {
    if (symbol && currentAccount && product) {
      mutation.mutate(
        {
          symbol,
          cancelOrders,
          accountID: currentAccount?.id,
          product: product,
        },
        {
          onSettled: () => onClose(),
        },
      );
    }
  };

  return (
    <div className="flex max-h-96 w-full flex-col gap-4 overflow-scroll rounded-lg">
      <div className="flex w-full flex-row items-center justify-between">
        <div className="flex flex-row flex-wrap-reverse gap-2">
          <Filter
            field="asset_class"
            setter={onBeforeSetFilter}
            defaultValue={filter?.asset_class}
            options={["all", "crypto", "us_equity", "us_option"]}
            optionsLabels={["All", "Crypto", "US Equity", "Options"]}
          />
          <Filter
            field="side"
            setter={onBeforeSetFilter}
            defaultValue={filter?.side}
            options={["all", "long", "short"]}
          />
        </div>
        <Button
          variant="ghost"
          onClick={() => history.push(Path.ROUTE_POSITIONS)}
        >
          View All
        </Button>
      </div>
      <DataGrid
        isLoading={!data}
        emptyMessage="No open positions. Place some trades to see this table populate."
        rows={data ?? []}
        columns={columns}
        getRowActions={(position: Position) => (
          <Button
            variant="ghost"
            onClick={() => onOpen(position)}
            type="button"
          >
            <Icon
              name="XCircle"
              className="text-gray-600"
              onClick={() => onOpen(position)}
            />
          </Button>
        )}
      />

      <Modal
        isOpen={disclosure.isOpen}
        onClose={onClose}
        title="Warning"
        controls={
          <div className="flex items-center justify-between gap-4">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={onClick} variant="red">
              Confirm
            </Button>
          </div>
        }
      >
        <div>
          <Text>
            Are you sure you want to liquidate your <b>{symbol}</b> position?
          </Text>
          <br />
          <Radio
            label={`Cancel all open orders for this symbol?`}
            name="liquidate_positions"
            isChecked={cancelOrders}
            onChange={onChange}
          />
        </div>
      </Modal>
    </div>
  );
};

export default Portfolio;
